import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { MapPin } from 'lucide-react'
import { api, type PlaceSuggestion } from '../../api/client'
import { useDebounced } from '../../hooks/use-debounced'

interface Props {
  value: PlaceSuggestion | null
  onChange: (place: PlaceSuggestion | null) => void
  placeholder?: string
  language?: 'ru' | 'en'
  className?: string
}

export function PlaceAutocomplete({
  value,
  onChange,
  placeholder = 'Город',
  language = 'ru',
  className,
}: Props) {
  const [text, setText] = useState(value?.name ?? '')
  const [open, setOpen] = useState(false)
  const debounced = useDebounced(text, 350)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Sync text when an outside `value` changes (e.g. on user load)
  useEffect(() => {
    setText(value?.name ?? '')
  }, [value])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const enabled = debounced.trim().length >= 2 && debounced !== (value?.name ?? '')

  const query = useQuery({
    queryKey: ['places', 'search', debounced, language],
    queryFn: () => api.searchPlaces(debounced.trim(), language),
    enabled,
    staleTime: 1000 * 60 * 60,
  })

  function pick(p: PlaceSuggestion) {
    onChange(p)
    setText(p.name)
    setOpen(false)
  }

  return (
    <div ref={wrapRef} className={clsx('relative', className)}>
      <input
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
          if (value) onChange(null)
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-sage-200 bg-white px-3 py-2 text-sage-800 outline-none focus:border-sage-500"
        autoComplete="off"
      />

      {value && (
        <div className="mt-1 flex items-center gap-2 text-xs text-sage-600">
          <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} aria-hidden />
          <span>{value.country ?? ''} · {value.timezone}</span>
          <button
            type="button"
            onClick={() => { onChange(null); setText('') }}
            className="ml-auto text-sage-500 underline-offset-2 hover:underline"
          >
            очистить
          </button>
        </div>
      )}

      {open && enabled && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-2xl border border-sage-200 bg-white shadow-lg">
          {query.isLoading && (
            <div className="px-3 py-2 text-sm text-sage-500">Ищу…</div>
          )}
          {query.isError && (
            <div className="px-3 py-2 text-sm text-copper">Не получилось найти. Попробуй ещё раз.</div>
          )}
          {query.data && query.data.length === 0 && (
            <div className="px-3 py-2 text-sm text-sage-500">Ничего не нашлось</div>
          )}
          {query.data && query.data.length > 0 && (
            <ul className="max-h-60 overflow-y-auto">
              {query.data.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => pick(p)}
                    className="block w-full px-3 py-2 text-left text-sm transition hover:bg-sage-50"
                  >
                    <span className="font-medium text-sage-800">{p.name}</span>
                    <span className="ml-1 text-xs text-sage-500">{p.country ?? ''}</span>
                    <span className="block text-[11px] text-sage-400">{p.displayName}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
